import React from 'react';
import { styled } from '@styledComponents';
import ReactHtmlParser from 'react-html-parser';

import { Box, BoxProps } from '@components/Box';
import { Columns } from '@components/Columns';
import { Text } from '@components/typography';
import { themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { JoykitIcon } from '@components/JoykitIcon';
import { useState } from 'react';

interface ColoredBlockSectionProps
  extends Readonly<{
    backgroundColor: BoxProps['backgroundColor'];
    sideHeaderText: string;
    sectionId?: string;
    isLeftAligned?: boolean;
    isToggleNeeded?: boolean;
    bottonBottom?: string;
  }> {}

const FaqTitleWrapper = styled(Columns.Column)`
  ${themeMediaQuery('below_sm')} {
    padding: ${themeSpace(7)} ${themeSpace(3)} ${themeSpace(11)};
  }
`;

const StyledText = styled(Text)<{ isLeftAligned: boolean }>`
  text-align: center;
  ${themeMediaQuery('sm')} {
    text-align: ${({ isLeftAligned }) => (isLeftAligned ? 'left' : 'center')};
  }
`;

const FaqColumnsWrapper = styled(Columns)`
  ${themeMediaQuery('below_sm')} {
    padding: ${themeSpace(0)};
  }
`;

const MainWrapper = styled(Box)<{ isCompleteFAQ: boolean; isToggleNeeded: boolean }>`
  height: ${({ isCompleteFAQ, isToggleNeeded }) => (!isCompleteFAQ && isToggleNeeded ? '700px' : 'auto')};
  overflow: hidden;
  ${themeMediaQuery('below_sm')} {
    height: max-content;
  }
  .faq-list > div > div:nth-of-type(1n + 4) {
    display: ${({ isCompleteFAQ, isToggleNeeded }) => (!isCompleteFAQ && isToggleNeeded ? 'none' : 'block')};
  }
  .read-more {
    display: ${({ isCompleteFAQ, isToggleNeeded }) => (!isCompleteFAQ && isToggleNeeded ? 'none' : 'flex')} !important;
  }
`;

const ToggleButtonWrapper = styled(Box)<{ isCompleteFAQ: boolean; bottonBottom?: string }>`
  position: absolute;
  width: 100%;
  height: 400px;
  bottom: ${({ bottonBottom }) => (bottonBottom ? bottonBottom : '0px')};
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) -57.72%, #ffffff 39.59%);
  z-index: 2;
  justify-content: center;
  align-items: center;
  padding-bottom: 50px;
  display: ${({ isCompleteFAQ }) => (!isCompleteFAQ ? 'flex' : 'none')};
  ${themeMediaQuery('below_sm')} {
    height: 200px;
    bottom: -10px;
  }
`;

const FAQWrapper = styled(Columns.Column)<{ isCompleteFAQ: boolean; isToggleNeeded: boolean }>`
  position: relative;
  height: ${({ isCompleteFAQ, isToggleNeeded }) => (!isCompleteFAQ && isToggleNeeded ? '700px' : 'auto')};
  overflow: hidden;
`;

const BorderdCircle = styled.button`
  width: 50px;
  height: 50px;
  border: 2px solid #5f2698;
  box-sizing: border-box;
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    border: 2px solid #7139bf;
  }
  &:focus {
    border: 2px solid #502080;
  }
`;

const StyledDownArrow = styled(JoykitIcon)`
  width: 26px;
  height: 13px;
`;

export const ColoredBlockSection: React.FC<ColoredBlockSectionProps> = ({
  children,
  backgroundColor,
  sideHeaderText,
  sectionId,
  isLeftAligned = false,
  isToggleNeeded = false,
  bottonBottom = '0px',
}) => {
  const id = sectionId ? sectionId : 'ColoredBlockSection';
  const [isCompleteFAQ, setFAQToggle] = useState(false);

  return (
    <>
      <MainWrapper
        isCompleteFAQ={isCompleteFAQ}
        isToggleNeeded={isToggleNeeded}
        backgroundColor={backgroundColor}
        paddingY={[10, 10, 12]}
        paddingX={[5, 5, 11]}
        id={id}
      >
        <FaqColumnsWrapper>
          <FaqTitleWrapper width="half">
            <StyledText tagName="h2" variant="hed72Hero" isLeftAligned={isLeftAligned}>
              {ReactHtmlParser(sideHeaderText)}
            </StyledText>
          </FaqTitleWrapper>
          <FAQWrapper isToggleNeeded={isToggleNeeded} isCompleteFAQ={isCompleteFAQ} width="half">
            {children}
            {isToggleNeeded ? (
              <ToggleButtonWrapper isCompleteFAQ={isCompleteFAQ} bottonBottom={bottonBottom}>
                <BorderdCircle onClick={() => setFAQToggle(!isCompleteFAQ)}>
                  <StyledDownArrow name="downArrow" color="linkDefault" />
                </BorderdCircle>
              </ToggleButtonWrapper>
            ) : null}
          </FAQWrapper>
        </FaqColumnsWrapper>
      </MainWrapper>
    </>
  );
};
